type DataRecord = Record<string, any[]>;
type DataFilterFunction = (field: any) => boolean;

class GenericDataHandler<T extends DataRecord> {
  isValidField(field: any): boolean {
    return typeof field === 'string' && field.trim() !== '';
  }

  isValidDate(field: any): boolean {
    return typeof field === 'string' && !Number.isNaN(Date.parse(field));
  }

  isValidZip(field: any): boolean {
    return typeof field === 'number' && field > 0;
  }

  isValidNumber(field: any): boolean {
    return typeof field === 'number' && field > 0;
  }

  isValidEmail(field: any): boolean {
    const emailRegex = /^[^\s@]+@[^\s@][^\s.@]*\.[^\s@]+$/;
    return typeof field === 'string' && emailRegex.test(field);
  }

  isValidPhone(field: any): boolean {
    const phoneRegex = /^\+?(\d{1,2})?\s?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/;
    return typeof field === 'string' && phoneRegex.test(field);
  }

  private convertToArray<U>(field: U | U[]): U[] {
    return Array.isArray(field) ? field : [field];
  }

  filterInvalidFields(
    data: Partial<T>,
    fieldValidators: Record<string, DataFilterFunction>,
  ): T {
    const filteredData = {} as any;
    for (const key in data) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        // eslint-disable-next-line @typescript-eslint/unbound-method
        const validator = fieldValidators[key] || this.isValidField;
        const fieldArray = this.convertToArray(data[key]);
        filteredData[key] = fieldArray.filter(validator) as T[Extract<
          keyof T,
          string
        >];
      }
    }
    return filteredData;
  }

  mergeData(
    existingData: T,
    incomingData: Partial<T>,
  ): { dataChanged: boolean; mergedData: T } {
    const { dataChanged, uniqueData } = this.ensureUniqueData(
      existingData,
      incomingData,
    );
    const mergedData = { ...existingData };

    for (const key in uniqueData) {
      if (Object.prototype.hasOwnProperty.call(uniqueData, key)) {
        mergedData[key] = [
          ...(existingData[key] || []),
          ...(uniqueData[key] as any[]),
        ] as T[Extract<keyof T, string>];
      }
    }

    return { dataChanged, mergedData };
  }

  calculateDiff(existingData: T, incomingData: Partial<T>): T {
    const { uniqueData } = this.ensureUniqueData(existingData, incomingData);
    return uniqueData;
  }

  ensureUniqueData(
    existingData: T,
    incomingData: Partial<T>,
  ): { dataChanged: boolean; uniqueData: T } {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const uniqueData = {} as T;
    let dataChanged = false;

    if (!incomingData) {
      return { dataChanged, uniqueData };
    }
    for (const key in incomingData) {
      if (
        Object.prototype.hasOwnProperty.call(incomingData, key) &&
        incomingData[key] &&
        incomingData[key].length > 0 &&
        typeof incomingData[key].filter === 'function'
      ) {
        const newValues = incomingData[key]?.filter(
          item => !existingData[key]?.includes(item),
        );
        if (newValues && newValues.length > 0) {
          uniqueData[key] = newValues as T[Extract<keyof T, string>];
          dataChanged = true;
        } else {
          uniqueData[key] = [] as any;
        }
      }
    }

    return { dataChanged, uniqueData };
  }
}

export { DataFilterFunction, DataRecord, GenericDataHandler };
